<template>
  <div>
    <!-- Header Section -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="users" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
          </div>
          <h3 class="mb-0 fw-bold">User Management</h3>
        </div>
      </div>
      <div class="col-md-4">
        <div class="d-flex justify-content-end">
          <button
            v-if="profile.role !== 'user'"
            class="btn btn-primary shadow-sm"
            @click="addUserAccounts"
          >
            <vue-feather type="user-plus" size="18" class="me-1"></vue-feather>
            Add User
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="font-size-125 color-gray-600">
              <vue-feather type="list" stroke="#652aa3" size="18" class="me-1"></vue-feather>
              Users ({{users.length}})
            </div>
            <UInput v-model="searchQuery" size="md" placeholder="Search user" class="mb-0 w-25 search-input"/>
          </div>

          <!-- Loading Spinner -->
          <div v-if="loading" class="text-center py-4">
            <Spinner spinner-class="mb-1" />
          </div>

          <!-- Users Table -->
          <div v-if="!loading && filteredUsers.length > 0" class="table-responsive">
            <table class="table table-hover align-middle modern-table">
              <thead>
                <tr>
                  <th scope="col" class="fw-semibold">#</th>
                  <th scope="col" class="fw-semibold">Name</th>
                  <th scope="col" class="fw-semibold">Phone</th>
                  <th scope="col" class="fw-semibold">Email</th>
                  <th scope="col" class="fw-semibold">Role</th>
                  <th scope="col" class="fw-semibold">Status</th>
                  <th scope="col" class="fw-semibold">Created</th>
                  <th scope="col" class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(user, index) in paginatedUsers" :key="index" class="user-row">
                  <td>{{ (dataTablePage - 1) * dataTablePerPage + index + 1 }}</td>
                  <td class="fw-medium">
                    <div class="d-flex align-items-center">
                      <div class="user-avatar me-2">
                        <vue-feather type="user" size="16" class="avatar-icon"></vue-feather>
                      </div>
                      {{ name(user) }}
                    </div>
                  </td>
                  <td>{{ user.phone }}</td>
                  <td>{{ user.email }}</td>
                  <td>
                    <span class="role-badge" :class="'role-' + user.role">{{ user.role }}</span>
                  </td>
                  <td>
                    <span :class="getStatusBadgeClass(user.status)">
                      {{ user.status }}
                    </span>
                  </td>
                  <td>{{ formatDate(user.created_at) }}</td>
                  <td class="text-end">
                    <button
                      v-if="profile.role !== 'user' && user.role !== 'employee'"
                      class="btn btn-sm btn-outline-primary edit-btn"
                      @click="navigateToUserEdit(user.id)"
                    >
                      <vue-feather type="edit-2" size="16" class="me-1"></vue-feather>
                      Edit
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State -->
          <div v-if="!loading && filteredUsers.length === 0" class="text-center py-4">
            <div class="empty-state">
              <div class="bg-light p-4 rounded-circle mb-3 d-inline-block">
                <vue-feather type="users" size="32" stroke="#6c757d"></vue-feather>
              </div>
              <h5>No Users Found</h5>
              <p class="text-muted">{{ searchQuery ? 'No users match your search criteria' : 'Start by adding your first user' }}</p>
              <button
                v-if="profile.role !== 'user' && !searchQuery"
                class="btn btn-primary"
                @click="addUserAccounts"
              >
                <vue-feather type="user-plus" size="16" class="me-1"></vue-feather>
                Add User
              </button>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="!loading && filteredUsers.length > 0" class="pagination-container d-flex flex-wrap justify-content-around pt-3 pb-4">
            <div class="col-md-3">
              <button class="btn btn-outline-dark text-uppercase shadow-sm" @click="$router.go(-1)">
                <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
                Back
              </button>
            </div>
            <div class="col-md-6 text-center">
              <p class="mb-2 small">Rows per page</p>
              <select class="form-select shadow-sm mx-auto rows-per-page" v-model="dataTablePerPage" data="">
                <option v-for="item in [5, 10, 25, 50, 100]" :key="item" :value="item">{{item}}</option>
              </select>
            </div>
            <div class="col-md-3">
              <Pagination v-model="dataTablePage" :pages="Math.ceil(filteredUsers.length/dataTablePerPage)">
                <template v-slot:next>
                  Next
                </template>
                <template v-slot:previous>
                  Prev
                </template>
              </Pagination>
            </div>
          </div>
        </Card>
      </div>
    </div>

    <!-- Add User Modal -->
    <Modal
      :open="showModal"
      @closed="closeModal"
    >
      <div class="modal-header border-0">
        <h5 class="modal-title m-0 d-flex align-items-center">
          Add User
        </h5>
        <button @click="closeModal" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveUser">
          <div class="mb-3">
            <label for="first_name" class="form-label">First Name <span class="text-danger">*</span></label>
            <input type="text" class="form-control" id="first_name" v-model="form.first_name" required>
          </div>
          <div class="mb-3">
            <label for="last_name" class="form-label">Last Name <span class="text-danger">*</span></label>
            <input type="text" class="form-control" id="last_name" v-model="form.last_name" required>
          </div>
          <div class="mb-3">
            <label for="email" class="form-label">Email <span class="text-danger">*</span></label>
            <input type="email" class="form-control" id="email" v-model="form.email" required>
          </div>
          <div class="mb-3">
            <label for="phone" class="form-label">Phone <span class="text-danger">*</span></label>
            <input type="text" class="form-control" id="phone" v-model="form.phone" required>
          </div>
          <div class="mb-3">
            <label for="role" class="form-label">Role <span class="text-danger">*</span></label>
            <select class="form-select" id="role" v-model="form.role" required>
              <option value="" disabled selected>Select role</option>
              <option value="primary">Primary</option>
              <option value="admin">Admin</option>
              <option value="finance">Finance</option>
              <option value="user">User</option>
            </select>
          </div>
          <div class="mb-3">
            <label for="status" class="form-label">Status <span class="text-danger">*</span></label>
            <select class="form-select" id="status" v-model="form.status" required>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div class="d-flex justify-content-end">
            <button type="button" class="btn btn-outline-secondary me-2" @click="closeModal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Save
            </button>
          </div>
        </form>
      </div>
    </Modal>
  </div>
</template>

<script>
import userService from "@/api/userService";
import formatDate from "../../utils/formatDate";
import {decrypt} from "@/utils/crypto";
import VueFeather from 'vue-feather';
import Pagination from "@/components/pagination/Pagination.vue";
import notify from '@/utils/notify';

export default {
  name: "User",
  components: { VueFeather, Pagination },

  data(){
    return {
      profile: {},
      loading: true,
      saving: false,
      users: [],
      searchQuery: '',
      dataTablePage: 1,
      dataTablePerPage: 10,
      showModal: false,
      form: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: '',
        status: 'active'
      }
    }
  },

  computed: {
    filteredUsers() {
      if (!this.searchQuery) {
        return this.users;
      }

      const query = this.searchQuery.toLowerCase();
      return this.users.filter(user => {
        const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
        return fullName.includes(query) ||
           user.email.toLowerCase().includes(query) ||
           user.phone.toLowerCase().includes(query) ||
           user.role.toLowerCase().includes(query);
      });
    },

    paginatedUsers() {
      const start = (this.dataTablePage - 1) * this.dataTablePerPage;
      const end = start + this.dataTablePerPage;
      return this.filteredUsers.slice(start, end);
    }
  },

  methods: {
    formatDate,
    getUsers: async function () {
      try {
        this.loading = true;
        this.users = await userService.getUsers();
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        this.loading = false;
      }
    },
    name (user) {
      return `${user.first_name} ${user.last_name}`
    },
    addUserAccounts() {
      this.form = {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: '',
        status: 'active'
      };
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
    },

    async saveUser() {
      try {
        this.saving = true;
        const response = await userService.createUser(this.form);
        if (response) {
          this.closeModal();
          await this.getUsers();
        }
      } catch (error) {
        notify.alert.error(error.response?.data?.message || 'Failed to create user');
        console.error(error);
      } finally {
        this.saving = false;
      }
    },

    navigateToUserEdit(userId) {
      this.$router.push(`/user/${userId}`);
    },

    getStatusBadgeClass(status) {
      switch (status) {
        case 'active':
          return 'badge bg-success';
        case 'inactive':
          return 'badge bg-danger';
        default:
          return 'badge bg-dark';
      }
    },
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
    await this.getUsers();
  },

  watch: {
    searchQuery: function() {
      // Reset to first page when search query changes
      this.dataTablePage = 1;
    }
  }
}
</script>

<style scoped>
.bg-primary-light {
  background-color: rgba(101, 42, 163, 0.1);
}

.bg-info-light {
  background-color: rgba(13, 202, 240, 0.1);
}

.text-primary {
  color: #652aa3 !important;
}

/* Modern Table Styles */
.modern-table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.modern-table thead {
  background-color: #f8f9fa;
}

.modern-table th {
  padding: 16px;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #e9ecef;
}

.modern-table td {
  padding: 16px;
  vertical-align: middle;
  border-bottom: 1px solid #e9ecef;
}

.user-row {
  transition: all 0.2s ease;
}

.user-row:hover {
  background-color: rgba(101, 42, 163, 0.03);
}

/* User Avatar */
.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgba(101, 42, 163, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-icon {
  color: #652aa3;
}

/* Role and Status Badges */
.role-badge, .status-badge {
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-block;
  text-transform: capitalize;
}

.role-super-admin {
  background-color: rgba(101, 42, 163, 0.1);
  color: #652aa3;
}

.role-primary {
  background-color: rgba(13, 202, 240, 0.1);
  color: #0dcaf0;
}

.role-admin {
  background-color: rgba(13, 110, 253, 0.1);
  color: #0d6efd;
}

.role-hr {
  background-color: rgba(32, 201, 151, 0.1);
  color: #20c997;
}

.role-finance {
  background-color: rgba(255, 193, 7, 0.1);
  color: #ffc107;
}

.role-employee {
  background-color: rgba(108, 117, 125, 0.1);
  color: #6c757d;
}

/* Edit Button */
.edit-btn {
  border-radius: 6px;
  box-shadow: 0 2px 5px rgba(101, 42, 163, 0.15);
  transition: all 0.2s ease;
}

.edit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(101, 42, 163, 0.2);
}

.empty-state {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.font-size-125 {
  font-size: 1.25rem;
}

.color-gray-600 {
  color: #6c757d;
}

.search-input {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.search-input:focus-within {
  box-shadow: 0 2px 10px rgba(101, 42, 163, 0.15);
}

.btn {
  transition: all 0.2s ease;
}

.btn:hover:not(.disabled) {
  transform: translateY(-2px);
}

.rows-per-page {
  width: 80px;
  margin: 0 auto;
}
</style>
